import { SilentSocketMigration, parseSocketFrame } from '@/lib/silent-socket-migration'
import { useCallback, useRef } from 'react'
import { usePersistStore } from '@/store/persist.store'
import { useSocketStore } from '@/store/socket.store'
import { useChatStore } from '@/store/chat.store'
// import { DEFAULT_TOKEN } from '@/lib/enums'
import { useSocketHandlers } from './useSocketHandlers'
import { createSocketHandlerMap } from './socketHandlerMap'
import { useChatContext } from '@/context/ChatProvider'
import { useConfig } from '@/context/ConfigProvider'
import { useLocale, useTranslations } from 'next-intl'
import { browserTimeZone } from './browserTimeZone'

/**
 * Hook to manage socket connection and authentication
 */
export const useSocketConnection = () => {
  const locale = useLocale()
  const t = useTranslations('chat')
  const { websocketUrl } = useConfig()
  const siteId = usePersistStore(state => state.siteId)
  const guestId = usePersistStore(state => state.guestId)
  const token = usePersistStore(state => state.token)
  const isGuestIdActive = usePersistStore(state => state.isGuestIdActive)
  const setUser = usePersistStore(state => state.setUser)

  const setSocketConnectionStatus = useSocketStore(state => state.setSocketConnectionStatus)
  const setIsAuthSuccess = useSocketStore(state => state.setIsAuthSuccess)
  const setIsReconnecting = useSocketStore(state => state.setIsReconnecting)
  const setIsMessageResponseWaiting = useSocketStore(state => state.setIsMessageResponseWaiting)
  const setRetryAttempt = useSocketStore(state => state.setRetryAttempt)

  const setMessages = useChatStore(state => state.setMessages)
  const isLsOnly = useChatStore(state => state.isLsOnly)
  const { scrollToBottom } = useChatContext()

  const wsRef = useRef<WebSocket | null>(null)
  const migrationRef = useRef<SilentSocketMigration | null>(null)
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Get all socket handlers
  const handlers = useSocketHandlers()
  const handlerMap = createSocketHandlerMap(handlers)

  /**
   * Build auth payload based on token type
   */
  const buildAuthPayload = useCallback(() => {
    const sessionId = usePersistStore.getState().sessionId
    const payload: any = {
      type: 'auth',
      token: token,
      site_id: siteId,
      lang: locale,
      timezone: browserTimeZone()
    }

    // If token is guest, guestId MUST be present
    if (guestId && isGuestIdActive) {
      payload.guest_id = guestId
    }

    // Add sessionId if exists
    if (sessionId) {
      payload.session_id = sessionId
    }

    if (isLsOnly) {
      payload.ls = true
    }

    return payload
  }, [token, siteId, locale, guestId, isGuestIdActive, isLsOnly])

  /**
   * Connect to WebSocket server
   */
  const connectWebSocket = useCallback(() => {
    migrationRef.current?.cancel()
    // Close existing connection if any
    if (wsRef.current) {
      wsRef.current.onclose = null // Prevent auto-reconnect trigger
      wsRef.current.close()
      wsRef.current = null
    }

    // Clear any pending reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }

    const retryAttempt = useSocketStore.getState().retryAttempt

    setUser(null)
    setIsReconnecting(retryAttempt > 0)

    // Validate token exists
    if (!token) {
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: t('missingCredentials'),
          type: 'error'
        }
      ])
      scrollToBottom()

      return
    }

    // Build auth payload
    const authPayload = buildAuthPayload()
    if (!authPayload) {
      setSocketConnectionStatus('error')
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: t('missingConnectionInfo'),
          type: 'error'
        }
      ])
      scrollToBottom()

      return
    }

    // Show loading message on first attempt
    if (retryAttempt === 0) {
      setMessages([
        {
          role: 'assistant',
          content: t('authenticationLoading'),
          type: 'info'
        }
      ])
      scrollToBottom()
    }

    try {
      const wsUrl = `${websocketUrl}${process.env.NODE_ENV === 'development' ? '' : '/api/chat'}`
      console.log('[WS] Connecting to:', wsUrl)
      const ws = new WebSocket(wsUrl)
      wsRef.current = ws

      ws.onopen = () => {
        console.log('[WS] Connected, sending auth')
        setSocketConnectionStatus('opened')
        ws.send(JSON.stringify(authPayload))
      }

      let historyReady = false
      let updatePending = false
      const receive = (data: any) => {
        if (data?.update === 'ready') {
          if (historyReady) migrationRef.current?.begin(wsRef.current!)
          else updatePending = true

          return
        }
        if (data?.update) return
        const eventType = data.type ?? data.event
        const isUploadFileAck = eventType === 'upload_file.success' || eventType === 'upload_file.error'
        if (!isUploadFileAck && !data.migration) setIsMessageResponseWaiting(false)
        const handler = handlerMap[eventType as keyof typeof handlerMap]
        if (handler) handler(data)
        if (eventType === 'message_history') {
          historyReady = true
          if (updatePending) {
            updatePending = false
            migrationRef.current?.begin(wsRef.current!)
          }
        }
      }
      const wireClose = (socket: WebSocket) => {
        socket.onclose = onClose
        socket.onerror = () => {
          if (migrationRef.current?.active || socket !== wsRef.current) return
          setSocketConnectionStatus('error')
          setIsAuthSuccess(false)
        }
      }
      migrationRef.current = new SilentSocketMigration({
        current: () => wsRef.current,
        open: () => new WebSocket(wsUrl),
        auth: buildAuthPayload,
        promote: socket => {
          wsRef.current = socket
          wireClose(socket)
        },
        receive,
        failed: message => console.error('[WS migration]', message)
      })
      ws.onmessage = event => {
        if (ws === wsRef.current) receive(parseSocketFrame(String(event.data)))
      }

      ws.onerror = err => {
        console.error('[WS] Error:', err)
      }

      const onClose = (event: CloseEvent) => {
        if (migrationRef.current?.active || event.target !== wsRef.current) return
        console.log('[WS] Closed:', event.code, event.reason)
        setSocketConnectionStatus('closed')
        setIsAuthSuccess(false)
        wsRef.current = null

        // Auto reconnect logic. Read the current attempt here because this
        // handler can run after the render that created this socket.
        const currentRetryAttempt = useSocketStore.getState().retryAttempt

        if (token && currentRetryAttempt < 10) {
          setIsReconnecting(true)
          setRetryAttempt(currentRetryAttempt + 1)

          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current)
          }

          const timeoutId = setTimeout(() => {
            connectWebSocket()
          }, 2000)
          reconnectTimeoutRef.current = timeoutId
        } else if (currentRetryAttempt >= 10) {
          setIsReconnecting(false)
          setSocketConnectionStatus('error')
          setMessages(prev => [
            ...prev,
            {
              role: 'assistant',
              content: t('maxReconnectExceeded'),
              type: 'error'
            }
          ])
          scrollToBottom()
        }
      }

      wireClose(ws)
      ws.onerror = event => {
        if (migrationRef.current?.active || ws !== wsRef.current) return
        setSocketConnectionStatus('error')
        setIsAuthSuccess(false)
        console.error('WebSocket error:', event)
      }
    } catch (error) {
      setSocketConnectionStatus('error')
      setIsAuthSuccess(false)
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: t('connectionFailed'),
          type: 'error'
        }
      ])
      scrollToBottom()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, siteId, guestId, isGuestIdActive, isLsOnly, buildAuthPayload, t])

  const disconnectWebSocket = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
    migrationRef.current?.cancel()
    migrationRef.current = null
    const socket = wsRef.current
    wsRef.current = null
    if (socket) {
      socket.onopen = null
      socket.onmessage = null
      socket.onerror = null
      socket.onclose = null
      socket.close(1000, 'Visitor ended session')
    }
    useChatStore.getState().setIsFirstButtonClick(false)
    useSocketStore.setState({
      socketConnectionStatus: 'closed',
      isAuthSuccess: false,
      isReconnecting: false,
      isMessageResponseWaiting: false,
      isPreparing: false,
      retryAttempt: 0
    })
  }, [])

  return {
    wsRef,
    sendFrame: (frame: string) => migrationRef.current?.send(frame),
    canSend: () => migrationRef.current?.canSend() ?? false,
    connectWebSocket,
    disconnectWebSocket
  }
}
